import { useId, useState } from 'react';
import { CornerDownRightIcon, PlusIcon, SearchIcon, XIcon } from 'lucide-react';

import type {
  BundleItem,
  BundlePreview,
  ShareOutcome,
} from '@/lib/api/generated/model';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxGroupLabel,
  ComboboxInput,
  ComboboxInputGroup,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox';
import { cn } from '@/lib/utils';

import { includesLine, type Choice, type TreeRow } from '../bundles/includes';

/**
 * A row's own action (spec 212): leave a clashing piece to the Library, or send
 * ours after all. It sits on the reason line it answers, outside the row's tick.
 */
export interface RowAction {
  label: "Use the Library's" | 'Send ours' | 'Leave it out' | 'Send it';
  /** The accessible name, with the piece's key. */
  name: string;
  onPress: () => void;
  disabled: boolean;
}

/** What happens to a row's piece when shared, or that it is not being sent. */
export interface RowOutcome {
  outcome: ShareOutcome | 'not_sent';
  reason?: string | null;
  /** On the reason line, in order: Use the Library's before Leave it out. */
  actions?: RowAction[];
}

const OUTCOME_LABEL: Record<RowOutcome['outcome'], string> = {
  added: 'Added',
  updated: 'Updated',
  unchanged: 'Unchanged',
  already_in_library: 'Already in the Library',
  refused: 'Refused',
  uses_library: "Uses the Library's",
  not_sent: 'Not sent',
};

/**
 * What travels, in one line (spec 211): "Includes 3 workflows · 6 agents named
 * in prompts · no skills". The usual export needs nothing ticked, so the
 * choice folds behind Change; in Share the choice is always open below, and the
 * line is its summary.
 */
export function IncludesLine({
  preview,
  choice,
  open,
  onToggle,
  usesLibrary = 0,
}: {
  preview: BundlePreview;
  choice: Choice;
  open?: boolean;
  /** Share: how many pieces are left to the Library's version (spec 212). */
  usesLibrary?: number;
  /** Export's Change / Done. */
  onToggle?: () => void;
}) {
  return (
    <div className="flex min-h-9 items-center gap-2 text-sm">
      <p className="min-w-0 flex-1">
        <span className="font-medium">Includes</span>{' '}
        {includesLine(preview, choice, usesLibrary)}
      </p>
      {onToggle && (
        <Button
          variant="ghost"
          size="sm"
          aria-expanded={open}
          onClick={onToggle}
        >
          {open ? 'Done' : 'Change'}
        </Button>
      )}
    </div>
  );
}

/**
 * The agents and skills that travel (dialogs review 2, slice 6).
 *
 * The ones a prompt names are listed, never ticked: they go with the workflow,
 * because a prompt that names a missing agent is a broken workflow wherever it
 * lands. Anything else is added on purpose from one search, "Add another agent
 * or skill…", and shows in the list with a × to take it out again. It replaced
 * two groups of ticks per kind (spec 211), whose named ticks could be cleared
 * and whose "Also in this workspace" group was the whole workspace.
 *
 * Share passes each row's outcome and its actions; a named piece the Library
 * refuses is answered there (Use the Library's, or Leave it out).
 */
export function IncludesChoice({
  preview,
  choice,
  onChange,
  outcomeOf,
}: {
  preview: BundlePreview;
  choice: Choice;
  onChange: (next: { agents: Set<string>; skills: Set<string> }) => void;
  /** Share: what will happen to each piece, and what the person can do. */
  outcomeOf?: (kind: PieceKind, key: string) => RowOutcome | null;
}) {
  const headingId = useId();
  if (preview.agents.length === 0 && preview.skills.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">
        No agents or skills in this workspace.
      </p>
    );
  }
  const chosen = (kind: PieceKind) =>
    kind === 'agent' ? choice.agents : choice.skills;
  const set = (kind: PieceKind, key: string, on: boolean) => {
    const agents = new Set(choice.agents);
    const skills = new Set(choice.skills);
    const target = kind === 'agent' ? agents : skills;
    if (on) target.add(key);
    else target.delete(key);
    onChange({ agents, skills });
  };
  const all = [...preview.agents, ...preview.skills];
  // Named pieces always have a row (one left out in Share keeps its place);
  // others only while added, after them, beside the way to add more.
  const rows = [
    ...all.filter((item) => item.suggested),
    ...all.filter((item) => !item.suggested && chosen(item.kind).has(item.key)),
  ];
  const addable = all.filter(
    (item) => !item.suggested && !chosen(item.kind).has(item.key),
  );
  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-1.5">
      <h3 id={headingId} className="text-sm font-medium">
        Agents and skills
      </h3>
      {rows.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          No prompt names an agent or a skill.
        </p>
      ) : (
        <ul
          className="flex flex-col gap-0.5"
          aria-label="Agents and skills sent"
        >
          {rows.map((item) => (
            <PieceRow
              key={`${item.kind}:${item.key}`}
              item={item}
              outcome={outcomeOf?.(item.kind, item.key) ?? null}
              onRemove={
                item.suggested
                  ? undefined
                  : () => set(item.kind, item.key, false)
              }
            />
          ))}
        </ul>
      )}
      {addable.length > 0 && (
        <AddPiece
          items={addable}
          onAdd={(item) => set(item.kind, item.key, true)}
        />
      )}
    </section>
  );
}

type PieceKind = BundleItem['kind'];

/**
 * One piece that travels, as a 36px row (44px under a coarse pointer): its key,
 * what it is, and why it goes. Share puts its outcome over the row's end and
 * its actions on the reason line under it.
 */
function PieceRow({
  item,
  outcome,
  onRemove,
}: {
  item: BundleItem;
  outcome: RowOutcome | null;
  /** Added by the person: taken out again with ×. Named pieces have none. */
  onRemove?: () => void;
}) {
  const reasonId = useId();
  return (
    <li className="flex flex-col">
      <div className="touch-target flex items-center gap-2 rounded-md px-2 py-2 text-sm">
        <span
          className="min-w-0 truncate"
          aria-describedby={outcome?.reason ? reasonId : undefined}
        >
          {item.key}
        </span>
        <span className="text-muted-foreground shrink-0 text-xs">
          · {item.kind}
          {item.suggested ? '' : ', added'}
        </span>
        <span className="ml-auto flex shrink-0 items-center gap-1">
          {outcome && <OutcomeBadge outcome={outcome.outcome} />}
          {onRemove && (
            <Button
              variant="ghost"
              size="icon-sm"
              // Inside the row's own padding, so a removable row is as tall as
              // the rest.
              className="-my-1.5"
              aria-label={`Remove ${item.key}`}
              onClick={onRemove}
            >
              <XIcon aria-hidden="true" />
            </Button>
          )}
        </span>
      </div>
      {outcome && (
        <RowReason outcome={outcome} reasonId={reasonId} className="pl-2" />
      )}
    </li>
  );
}

/**
 * "Add another agent or skill…": the app's combobox over the pieces no prompt
 * names, grouped by kind (dialogs review 2, slice 6 follow-up).
 *
 * Its list floats over the dialog, so the dialog's body stays the one thing that
 * scrolls: an inline list scrolling inside it showed two scrollbars side by side
 * and clipped its first row. Arrow keys move through the matches and Enter adds
 * the highlighted one; each match carries a plus, so it reads as something to
 * add, not as a piece already sent. The search stays after a pick, for the
 * next one; the × in the field closes it.
 */
function AddPiece({
  items,
  onAdd,
}: {
  items: BundleItem[];
  onAdd: (item: BundleItem) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  if (!open) {
    return (
      <Button
        variant="ghost"
        size="sm"
        className="w-fit"
        onClick={() => setOpen(true)}
      >
        <PlusIcon aria-hidden="true" />
        Add another agent or skill…
      </Button>
    );
  }
  const idOf = (item: BundleItem) => `${item.kind}:${item.key}`;
  const needle = query.trim().toLocaleLowerCase();
  const shown = items.filter((item) =>
    item.key.toLocaleLowerCase().includes(needle),
  );
  const groups = (
    [
      ['Agents', shown.filter((item) => item.kind === 'agent')],
      ['Skills', shown.filter((item) => item.kind === 'skill')],
    ] as const
  ).filter(([, group]) => group.length > 0);
  const close = () => {
    setOpen(false);
    setQuery('');
  };
  return (
    <div className="flex items-center gap-2">
      <div className="min-w-0 flex-1">
        <Combobox<string, false>
          defaultOpen
          openOnInputClick
          autoHighlight
          value={null}
          onValueChange={(id) => {
            const item = items.find((candidate) => idOf(candidate) === id);
            if (!item) return;
            // The search stays, ready for another; its list closes itself.
            // Unmounting it here, list still open, left the dialog hidden
            // from assistive technology.
            onAdd(item);
            setQuery('');
          }}
          inputValue={query}
          onInputValueChange={(next) => setQuery(next)}
          items={shown.map(idOf)}
          itemToStringLabel={(id) => id.slice(id.indexOf(':') + 1)}
        >
          <ComboboxInputGroup>
            <SearchIcon
              aria-hidden="true"
              strokeWidth={1.5}
              className="text-muted-foreground size-4 shrink-0"
            />
            <ComboboxInput
              aria-label="Find an agent or skill to add"
              placeholder="Find an agent or skill"
              autoFocus
            />
            {/* Closing sits in the field, as a clear button does: a second
                "Done" beside the Includes line's own read as two different
                endings for one choice. */}
            <Button
              variant="ghost"
              size="icon-xs"
              className="-my-1 shrink-0"
              aria-label="Stop adding"
              onClick={close}
            >
              <XIcon aria-hidden="true" />
            </Button>
          </ComboboxInputGroup>
          <ComboboxContent>
            <ComboboxList>
              {groups.map(([label, group]) => (
                <ComboboxGroup key={label} items={group.map(idOf)}>
                  <ComboboxGroupLabel>{label}</ComboboxGroupLabel>
                  {group.map((item) => (
                    <ComboboxItem key={idOf(item)} value={idOf(item)}>
                      <PlusIcon
                        aria-hidden="true"
                        className="text-muted-foreground size-3.5 shrink-0"
                      />
                      <span className="truncate">{item.key}</span>
                    </ComboboxItem>
                  ))}
                </ComboboxGroup>
              ))}
            </ComboboxList>
            {shown.length === 0 && (
              <ComboboxEmpty>
                <span className="text-muted-foreground text-sm">
                  Nothing else matches “{query.trim()}”.
                </span>
              </ComboboxEmpty>
            )}
          </ComboboxContent>
        </Combobox>
      </div>
    </div>
  );
}

/**
 * The lines under a row: why, then the row's action when it has one. The action
 * keeps its own line, so its place never depends on how long the reason is. A
 * refusal reads as one; using the Library's version is a choice, so it reads
 * quietly.
 */
function RowReason({
  outcome,
  reasonId,
  className,
}: {
  outcome: RowOutcome;
  reasonId?: string;
  className?: string;
}) {
  const { reason } = outcome;
  const actions = outcome.actions ?? [];
  if (!reason && actions.length === 0) return null;
  return (
    <div className={cn('flex flex-col items-start pb-1 text-xs', className)}>
      {reason && (
        <p
          id={reasonId}
          className={cn(
            'min-w-0',
            // A row with an action swaps a refusal for "Uses the Library's…"
            // and back: room kept for the longer (three lines at phone width,
            // two above), so the rows below stay put.
            actions.length > 0 && 'min-h-[3lh] sm:min-h-[2lh]',
            outcome.outcome === 'uses_library' || outcome.outcome === 'not_sent'
              ? 'text-muted-foreground'
              : 'text-destructive',
          )}
        >
          {reason}
        </p>
      )}
      {actions.length > 0 && (
        // The first label lines up with the reason above it.
        <div className="-ml-2.5 flex flex-wrap gap-1">
          {actions.map((action) => (
            <Button
              key={action.label}
              type="button"
              variant="ghost"
              size="sm"
              aria-label={action.name}
              disabled={action.disabled}
              onClick={action.onPress}
            >
              {action.label}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}

export function OutcomeBadge({
  outcome,
  id,
}: {
  outcome: RowOutcome['outcome'];
  id?: string;
}) {
  return (
    <Badge
      id={id}
      variant={outcome === 'refused' ? 'destructive' : 'outline'}
      className={cn(
        (outcome === 'not_sent' || outcome === 'uses_library') &&
          'text-muted-foreground',
      )}
    >
      {OUTCOME_LABEL[outcome]}
    </Badge>
  );
}

/**
 * The workflows that travel, as a tree from the root (spec 211 US3): each one
 * under the workflow that links to it, indented by its depth.
 */
export function WorkflowTree({
  rows,
  outcomeOf,
}: {
  rows: TreeRow[];
  /** Share: what will happen to each workflow. */
  outcomeOf?: (key: string) => RowOutcome | null;
}) {
  const headingId = useId();
  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-1.5">
      <h3 id={headingId} className="text-sm font-medium">
        {rows.length === 1 ? 'Workflow' : `Workflows (${String(rows.length)})`}
      </h3>
      <ul className="flex flex-col text-sm">
        {rows.map(({ workflow, depth }) => {
          const outcome = outcomeOf?.(workflow.key) ?? null;
          return (
            <li
              key={workflow.key}
              data-depth={depth}
              style={{ paddingInlineStart: depth * 16 }}
              className="flex flex-col"
            >
              <span className="flex min-h-7 items-center gap-1.5">
                {depth > 0 && (
                  <CornerDownRightIcon
                    aria-hidden="true"
                    className="text-muted-foreground size-3.5 shrink-0"
                  />
                )}
                <span className="truncate">{workflow.name}</span>
                {workflow.has_pending_changes && (
                  <span className="text-muted-foreground shrink-0 text-xs">
                    · has unpublished changes
                  </span>
                )}
                {outcome && (
                  <span className="ml-auto shrink-0">
                    <OutcomeBadge outcome={outcome.outcome} />
                  </span>
                )}
              </span>
              {outcome && <RowReason outcome={outcome} />}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
